import React, { useEffect, useState } from "react";
import TrainerListing from "./TrainerListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setTotalItems,
} from "src/redux/slices/TrainerSlice";
import { RxPencil1 } from "react-icons/rx";
import { MdDeleteOutline } from "react-icons/md";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import {
  useDeleteTrainerMutation,
  useGetAllTrainerWithPaginationQuery,
} from "src/services/TrainerServices";
import { showToast } from "src/utils/showToaster";
import { useNavigate } from "react-router-dom";
import { Chip } from "@mui/material";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import ChangeTrainerPasswordWrapper from "./ChangePassword/ChangeTrainerPasswordWrapper";
import { TbPasswordUser } from "react-icons/tb";

type Props = {};

const TrainerListingWrapper = (props: Props) => {
  const navigate = useNavigate();

  const [isOpenChangePasswordDialog, setIsOpenChangePasswordDialog] =
    useState(false);
  const [trainerId, setTrainerId] = useState("");

  const [deleteTrainer] = useDeleteTrainerMutation();
  const dispatch = useDispatch();

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.trainer);

  const { isLoading, isFetching, data } = useGetAllTrainerWithPaginationQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: [
      "name",
      "email",
      "mobile",
      "altMobile",
      "whatsappMobileNo",
      "emergencyContactNo",
      "address",
      "gender",
      "dob",
      "maritalStatus",
      "academicHistory",
      "workExperience",
      "bloodGroup",
    ],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
  }, [isLoading, isFetching, data, dispatch]);

  const columns: columnTypes[] = [
    {
      field: "createdAt",
      headerName: "Date - Time",
      extraClasses: "py-1",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatedDateTimeIntoIst(row.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatedDateTimeIntoIst(row.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "mobile",
      headerName: "Number",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "branchId",
      headerName: " Branch Name",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: any) => {
        return row?.branchId?.map((branch: any) => {
          return (
            <Chip
              label={branch?.branchName}
              key={branch?._id}
              className="me-2 my-2"
            />
          );
        });
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[220px]",
    },
    {
      field: "address",
      headerName: "address",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },
    {
      noAuthRequired: true,
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        const options = getActionOptions(row);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  const getActionOptions = (row: any) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          navigate(`edit/${row?._id}`);
        },
        actionName: UserModuleActionTypes.EDIT,
        moduleName: UserModuleNameTypes.TRAINER,
      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to Delete this Trainer ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteTrainer(row?._id).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.DELETE,
        moduleName: UserModuleNameTypes.TRAINER,
      },
      {
        label: (
          <div className="flex gap-2 items-center ">
            <TbPasswordUser className="text-lg" />
            <span className="text-[15px]  font-medium">
              Change Password
            </span>
          </div>
        ),
        onClick: () => {
          setIsOpenChangePasswordDialog(true);
          setTrainerId(row?._id);
        },
        actionName: UserModuleActionTypes.TRAINER_PASSWORD_CHANGE,
        moduleName: UserModuleNameTypes.TRAINER,
      },
    ];
  };
  return (
    <div>
      <TrainerListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.TRAINER,
          actionName: UserModuleActionTypes.LIST,
          columns: columns,
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
      {isOpenChangePasswordDialog && (
        <ChangeTrainerPasswordWrapper
        trainerId={trainerId}
          onClose={() => {
            setIsOpenChangePasswordDialog(false);
          }}
          setTrainerId={setTrainerId}
        />
      )}
    </div>
  );
};

export default TrainerListingWrapper;
